import { useEffect, useMemo, useState } from 'react';
import {
  addDoc,
  arrayUnion,
  collection,
  doc,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  updateDoc
} from 'firebase/firestore';
import {
  CheckCircle2,
  Clock3,
  Filter,
  MessageCircleMore,
  Search,
  Send,
  UserRound
} from 'lucide-react';
import PageHeader from '../../components/ui/PageHeader';
import { useAuth } from '../../core/auth/AuthProvider';
import { db } from '../../core/firebase/firebase';
import { PERMISSIONS } from '../../core/permissions/roles';

const normalize = (value) => String(value || '').trim().toLowerCase();

const STATUS_LABELS = {
  sent: 'Nouvelle',
  in_review: 'En cours',
  waiting_parent: 'Attente parent',
  resolved: 'Traitée',
  closed: 'Fermée'
};

const CATEGORY_LABELS = {
  administration: 'Administration',
  pedagogie: 'Pédagogie',
  paiement: 'Paiement',
  autre: 'Autre'
};

function canonicalStatus(value) {
  const status = normalize(value);
  if (['sent', 'envoyee', 'envoyée', 'nouvelle', 'new'].includes(status)) return 'sent';
  if (['in_review', 'en_cours', 'encours', 'processing', 'en traitement'].includes(status)) return 'in_review';
  if (['waiting_parent', 'attente_parent', 'en attente parent'].includes(status)) return 'waiting_parent';
  if (['resolved', 'done', 'traitee', 'traitée'].includes(status)) return 'resolved';
  if (['closed', 'fermee', 'fermée', 'archivee', 'archivée'].includes(status)) return 'closed';
  return status || 'sent';
}

function toMillis(value) {
  if (!value) return 0;
  if (typeof value?.toMillis === 'function') return value.toMillis();
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatDate(value, withTime = false) {
  const ms = toMillis(value);
  if (!ms) return 'Date à confirmer';
  return new Intl.DateTimeFormat('fr-TN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {})
  }).format(ms);
}

function statusLabel(value) {
  return STATUS_LABELS[canonicalStatus(value)] || value || 'Nouvelle';
}

function categoryLabel(value) {
  return CATEGORY_LABELS[normalize(value)] || value || 'Autre';
}

function adminDisplayName(profile) {
  return profile?.displayName || profile?.fullName || profile?.fullname || profile?.name || 'Administration';
}

export default function CommunicationPage() {
  const { user, profile, can } = useAuth();
  const canReply = can(PERMISSIONS.COMMUNICATION_SEND);
  const [complaints, setComplaints] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [messages, setMessages] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('open');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [reply, setReply] = useState('');
  const [replyStatus, setReplyStatus] = useState('in_review');
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'reclamations'),
      (snapshot) => {
        const rows = snapshot.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .sort((a, b) => toMillis(b.updatedAt || b.createdAt) - toMillis(a.updatedAt || a.createdAt));
        setComplaints(rows);
        setSelectedId((current) => current && rows.some((item) => item.id === current) ? current : rows[0]?.id || '');
      },
      (err) => {
        console.error('Communication — réclamations:', err);
        setError('Impossible de charger les réclamations.');
      }
    );
    return unsubscribe;
  }, []);

  const selected = useMemo(
    () => complaints.find((item) => item.id === selectedId) || null,
    [complaints, selectedId]
  );

  useEffect(() => {
    setMessages([]);
    if (!selectedId) return undefined;
    return onSnapshot(
      collection(db, 'reclamations', selectedId, 'messages'),
      (snapshot) => {
        const rows = snapshot.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .sort((a, b) => toMillis(a.createdAt) - toMillis(b.createdAt));
        setMessages(rows);
      },
      (err) => console.error('Communication — messages:', err)
    );
  }, [selectedId]);

  const filtered = useMemo(() => {
    const needle = normalize(search);
    return complaints.filter((item) => {
      const status = canonicalStatus(item.status);
      const statusOk = statusFilter === 'all'
        || (statusFilter === 'open' && !['resolved', 'closed'].includes(status))
        || status === statusFilter;
      const categoryOk = categoryFilter === 'all' || normalize(item.category) === categoryFilter;
      const text = normalize([
        item.subject,
        item.message,
        item.parentName,
        item.parentNom,
        item.studentName,
        item.category,
        item.id
      ].filter(Boolean).join(' '));
      return statusOk && categoryOk && (!needle || text.includes(needle));
    });
  }, [complaints, search, statusFilter, categoryFilter]);

  useEffect(() => {
    if (!filtered.length) {
      if (selectedId) setSelectedId('');
      return;
    }
    if (!filtered.some((item) => item.id === selectedId)) setSelectedId(filtered[0].id);
  }, [filtered, selectedId]);

  const stats = useMemo(() => ({
    new: complaints.filter((item) => canonicalStatus(item.status) === 'sent').length,
    open: complaints.filter((item) => !['resolved', 'closed'].includes(canonicalStatus(item.status))).length,
    waitingParent: complaints.filter((item) => canonicalStatus(item.status) === 'waiting_parent').length,
    resolved: complaints.filter((item) => canonicalStatus(item.status) === 'resolved').length
  }), [complaints]);

  async function setStatus(nextStatus) {
    if (!selected || !canReply || saving || canonicalStatus(selected.status) === nextStatus) return;
    setSaving(true);
    setError('');
    try {
      await updateDoc(doc(db, 'reclamations', selected.id), {
        status: nextStatus,
        updatedAt: serverTimestamp(),
        statusHistory: arrayUnion({
          status: nextStatus,
          at: Timestamp.now(),
          byUid: user.uid,
          byName: adminDisplayName(profile),
          byType: 'administration'
        })
      });
      setNotice(`Statut mis à jour : ${statusLabel(nextStatus)}.`);
    } catch (err) {
      console.error(err);
      setError('Impossible de modifier le statut.');
    } finally {
      setSaving(false);
    }
  }

  async function sendReply(event) {
    event.preventDefault();
    const textValue = reply.trim();
    if (!selected || !canReply || !textValue || saving) return;
    setSaving(true);
    setError('');
    try {
      await addDoc(collection(db, 'reclamations', selected.id, 'messages'), {
        text: textValue,
        authorType: 'administration',
        authorUid: user.uid,
        authorName: adminDisplayName(profile),
        parentId: selected.parentId || null,
        createdAt: serverTimestamp()
      });

      await updateDoc(doc(db, 'reclamations', selected.id), {
        status: replyStatus,
        updatedAt: serverTimestamp(),
        lastMessageAt: serverTimestamp(),
        lastMessageBy: 'administration',
        statusHistory: arrayUnion({
          status: replyStatus,
          at: Timestamp.now(),
          byUid: user.uid,
          byName: adminDisplayName(profile),
          byType: 'administration'
        })
      });
      setReply('');
      setNotice('Réponse envoyée au parent.');
    } catch (err) {
      console.error(err);
      setError('Impossible d’envoyer la réponse.');
    } finally {
      setSaving(false);
    }
  }

  const selectedStatus = canonicalStatus(selected?.status);
  const history = Array.isArray(selected?.statusHistory) ? [...selected.statusHistory] : [];
  history.sort((a, b) => toMillis(a.at) - toMillis(b.at));

  return <>
    <PageHeader
      eyebrow="COMMUNICATION"
      title="Réclamations"
      description="Traitez les demandes des familles dans une boîte de réception claire et centralisée."
    />

    {notice && <div className="alert alert-success communication-alert"><span>{notice}</span><button onClick={() => setNotice('')}>×</button></div>}
    {error && <div className="alert alert-error communication-alert"><span>{error}</span><button onClick={() => setError('')}>×</button></div>}

    <section className="complaint-admin-overview-v2">
      <div className="complaint-admin-overview-copy-v2">
        <strong>{stats.open} demande{stats.open > 1 ? 's' : ''} à suivre</strong>
        <span>{stats.new ? `${stats.new} nouvelle${stats.new > 1 ? 's' : ''}` : 'Aucune nouvelle demande'} · {stats.waitingParent} en attente d’un parent</span>
      </div>
      <div className="complaint-admin-overview-badges-v2">
        <span><Clock3 size={15}/>{stats.open} ouvertes</span>
        <span><CheckCircle2 size={15}/>{stats.resolved} traitées</span>
      </div>
    </section>

    <section className="complaint-admin-shell-v2">
      <aside className="complaint-admin-inbox-v2">
        <div className="complaint-admin-filters-v2">
          <label className="complaint-search-v2">
            <Search size={17}/>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher une demande…" />
          </label>
          <div className="complaint-admin-tabs-v2">
            {[
              ['open', 'À traiter', stats.open],
              ['sent', 'Nouvelles', stats.new],
              ['waiting_parent', 'Attente parent', stats.waitingParent],
              ['resolved', 'Traitées', stats.resolved],
              ['all', 'Toutes', complaints.length]
            ].map(([value, label, count]) => <button
              key={value}
              className={statusFilter === value ? 'active' : ''}
              onClick={() => setStatusFilter(value)}
            >{label}<span>{count}</span></button>)}
          </div>
          <label className="complaint-category-filter-v2">
            <Filter size={15}/>
            <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
              <option value="all">Toutes les catégories</option>
              <option value="administration">Administration</option>
              <option value="pedagogie">Pédagogie</option>
              <option value="paiement">Paiement</option>
              <option value="autre">Autre</option>
            </select>
          </label>
        </div>

        <div className="complaint-admin-list-v2">
          {filtered.map((item) => {
            const status = canonicalStatus(item.status);
            const isNew = status === 'sent';
            return <button
              key={item.id}
              className={`complaint-admin-list-item-v2 ${selectedId === item.id ? 'selected' : ''} ${isNew ? 'is-new' : ''}`}
              onClick={() => setSelectedId(item.id)}
            >
              <div className="complaint-admin-item-top-v2">
                <span className={`complaint-status-v2 status-${status}`}>{statusLabel(status)}</span>
                <time>{formatDate(item.updatedAt || item.createdAt)}</time>
              </div>
              <strong>{item.subject || 'Réclamation sans objet'}</strong>
              <span>{item.parentName || item.parentNom || 'Parent'}{item.studentName ? ` · ${item.studentName}` : ''}</span>
              <small>{categoryLabel(item.category)}</small>
            </button>;
          })}
          {!filtered.length && <div className="complaint-empty-v2">
            <CheckCircle2 size={30}/>
            <strong>Rien à afficher</strong>
            <span>Aucune réclamation ne correspond aux filtres sélectionnés.</span>
          </div>}
        </div>
      </aside>

      <article className="complaint-admin-detail-v2">
        {!selected ? <div className="complaint-empty-v2 large">
          <MessageCircleMore size={36}/>
          <strong>Sélectionnez une réclamation</strong>
          <span>Le dossier et la conversation apparaîtront ici.</span>
        </div> : <>
          <header className="complaint-admin-detail-head-v2">
            <div className="complaint-admin-title-v2">
              <div className="complaint-admin-kicker-v2">
                <span className="complaint-category-label-v2">{categoryLabel(selected.category)}</span>
                <span>{formatDate(selected.createdAt, true)}</span>
              </div>
              <h2>{selected.subject || 'Réclamation'}</h2>
              <div className="complaint-party-inline-v2">
                <span><UserRound size={15}/><strong>{selected.parentName || selected.parentNom || 'Parent'}</strong></span>
                {selected.studentName && <span>Élève : <strong>{selected.studentName}</strong></span>}
              </div>
            </div>
            <div className="complaint-admin-status-box-v2">
              <span className={`complaint-status-v2 status-${selectedStatus}`}>{statusLabel(selectedStatus)}</span>
              {canReply && <label>
                <span>Changer le statut</span>
                <select value={selectedStatus} onChange={(e) => setStatus(e.target.value)} disabled={saving}>
                  <option value="sent">Nouvelle</option>
                  <option value="in_review">En cours</option>
                  <option value="waiting_parent">Attente parent</option>
                  <option value="resolved">Traitée</option>
                  <option value="closed">Fermée</option>
                </select>
              </label>}
            </div>
          </header>

          {selectedStatus === 'sent' && canReply && <div className="complaint-callout-v2 admin-new">
            <MessageCircleMore size={20}/>
            <div><strong>Nouvelle demande à traiter</strong><span>Prenez-la en charge pour indiquer à la famille que son dossier est en cours de traitement.</span></div>
            <button className="button button-primary" onClick={() => setStatus('in_review')} disabled={saving}>Prendre en charge</button>
          </div>}

          {selectedStatus === 'waiting_parent' && <div className="complaint-callout-v2 attention">
            <Clock3 size={20}/>
            <div><strong>En attente du parent</strong><span>L’administration a demandé une information complémentaire. Le dossier reprendra dès sa réponse.</span></div>
          </div>}

          <section className="complaint-thread-v2 admin-thread-v2">
            <div className="complaint-thread-title-v2"><strong>Conversation</strong><span>{messages.length + 1} message{messages.length ? 's' : ''}</span></div>
            <div className="complaint-message-v2 from-parent">
              <div className="complaint-message-meta-v2"><strong>{selected.parentName || selected.parentNom || 'Parent'}</strong><time>{formatDate(selected.createdAt, true)}</time></div>
              <p>{selected.message || 'Aucun message.'}</p>
            </div>
            {messages.map((message) => <div key={message.id} className={`complaint-message-v2 ${message.authorType === 'parent' ? 'from-parent' : 'from-admin'}`}>
              <div className="complaint-message-meta-v2"><strong>{message.authorName || (message.authorType === 'parent' ? 'Parent' : 'Administration')}</strong><time>{formatDate(message.createdAt, true)}</time></div>
              <p>{message.text}</p>
            </div>)}
            {!messages.length && selected.adminResponse && <div className="complaint-message-v2 from-admin">
              <div className="complaint-message-meta-v2"><strong>Administration</strong><time>{formatDate(selected.respondedAt, true)}</time></div>
              <p>{selected.adminResponse}</p>
            </div>}
          </section>

          {canReply && selectedStatus !== 'closed' && <form className="complaint-admin-composer-v2" onSubmit={sendReply}>
            <label>Répondre à la famille
              <textarea
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder="Écrivez une réponse claire et concise…"
                required
              />
            </label>

            <div className="complaint-after-send-v2">
              <span>Après l’envoi :</span>
              <div>
                {[
                  ['in_review', 'Continuer le traitement'],
                  ['waiting_parent', 'Attendre le parent'],
                  ['resolved', 'Marquer comme traitée']
                ].map(([value, label]) => <button
                  type="button"
                  key={value}
                  className={replyStatus === value ? 'active' : ''}
                  onClick={() => setReplyStatus(value)}
                >{label}</button>)}
              </div>
            </div>

            <div className="complaint-admin-composer-actions-v2">
              <span>{replyStatus === 'waiting_parent' ? 'Le parent verra qu’une réponse est attendue.' : replyStatus === 'resolved' ? 'Le dossier sera marqué comme traité.' : 'Le dossier restera en cours.'}</span>
              <button className="button button-primary" disabled={saving || !reply.trim()}>
                <Send size={16}/>{saving ? 'Envoi…' : 'Envoyer la réponse'}
              </button>
            </div>
          </form>}

          <details className="complaint-history-v2 admin-history-v2">
            <summary>Historique du dossier</summary>
            <div className="complaint-history-list-v2">
              <div><i/><p><strong>Réclamation reçue</strong><span>{formatDate(selected.createdAt, true)}</span></p></div>
              {history.filter((event) => canonicalStatus(event.status) !== 'sent').map((event, index) => <div key={`${toMillis(event.at)}-${index}`}>
                <i/><p><strong>{statusLabel(event.status)}</strong><span>{formatDate(event.at, true)}{event.byName ? ` · ${event.byName}` : ''}</span></p>
              </div>)}
            </div>
          </details>
        </>}
      </article>
    </section>
  </>;
}
